import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const rateCards = [
  { key: "single", title: "Single Ank", subtitle: "Single digit bet", color: "#eff6ff", border: "#93c5fd", code: "SA" },
  { key: "single_bulk", title: "Single Bulk", subtitle: "Bulk single digit", color: "#ecfeff", border: "#67e8f9", code: "SB" },
  { key: "jodi", title: "Jodi", subtitle: "Two digit pair", color: "#f0fdf4", border: "#86efac", code: "JD" },
  { key: "jodi_bulk", title: "Jodi Bulk", subtitle: "Bulk two digit pairs", color: "#fdf2f8", border: "#f9a8d4", code: "JB" },
  { key: "red_bracket", title: "Red Bracket", subtitle: "Bracket family bet", color: "#fff1f2", border: "#fda4af", code: "RB" },
  { key: "sp", title: "Single Patti", subtitle: "Three digit combination", color: "#fefce8", border: "#fde047", code: "SP" },
  { key: "sp_bulk", title: "SP Bulk", subtitle: "Bulk single patti", color: "#f5f3ff", border: "#c4b5fd", code: "SBK" },
  { key: "sp_motor", title: "SP Motor", subtitle: "Single patti motor", color: "#faf5ff", border: "#d8b4fe", code: "SM" },
  { key: "dp", title: "Double Patti", subtitle: "Two same digits", color: "#fff7ed", border: "#fdba74", code: "DP" },
  { key: "dp_bulk", title: "DP Bulk", subtitle: "Bulk double patti", color: "#fff7ed", border: "#fb923c", code: "DBK" },
  { key: "dp_motor", title: "DP Motor", subtitle: "Double patti motor", color: "#fef2f2", border: "#fca5a5", code: "DM" },
  { key: "tp", title: "Triple Patti", subtitle: "Three same digits", color: "#fef2f2", border: "#fca5a5", code: "TP" },
  { key: "half_sangam", title: "Half Sangam", subtitle: "Open/Close + Patti", color: "#eef2ff", border: "#a5b4fc", code: "HS" },
  { key: "full_sangam", title: "Full Sangam", subtitle: "Open + Close Patti", color: "#fdf2f8", border: "#f9a8d4", code: "FS" },
];

const starlineRateCards = [
  { key: "single", title: "Single Ank", subtitle: "Starline single digit", color: "#eff6ff", border: "#93c5fd", code: "SA" },
  { key: "sp", title: "Single Patti", subtitle: "Starline single patti", color: "#fefce8", border: "#fde047", code: "SP" },
  { key: "dp", title: "Double Patti", subtitle: "Starline double patti", color: "#fff7ed", border: "#fdba74", code: "DP" },
  { key: "tp", title: "Triple Patti", subtitle: "Starline triple patti", color: "#fef2f2", border: "#fca5a5", code: "TP" },
];

function AdminGameRates() {
  const [tab, setTab] = useState("normal");
  const [normalForm, setNormalForm] = useState({});
  const [starlineForm, setStarlineForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/admin/game-rates?market_type=normal")
      .then((res) => setNormalForm(res.data || {}))
      .catch(() => {});
    API.get("/admin/game-rates?market_type=starline")
      .then((res) => setStarlineForm(res.data || {}))
      .catch(() => {});
  }, []);

  const isStarline = tab === "starline";
  const cards = isStarline ? starlineRateCards : rateCards;
  const form = isStarline ? starlineForm : normalForm;
  const setForm = isStarline ? setStarlineForm : setNormalForm;

  const saveRates = async () => {
    try {
      setSaving(true);
      await API.post(`/admin/game-rates?market_type=${tab}`, form);
      alert(`${isStarline ? "Starline" : "Normal market"} rates saved`);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save game rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div style={{ borderLeft: "4px solid #4f46e5", paddingLeft: "14px" }}>
            <h1 style={title}>Game Rates</h1>
            <p style={subtitle}>Set payout multipliers for each bet type</p>
          </div>
          <button type="button" style={saveButton} onClick={saveRates} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save Rates"}
          </button>
        </div>

        {/* Tabs */}
        <div style={tabRow}>
          <button type="button" style={tab === "normal" ? tabActive : tabBtn} onClick={() => setTab("normal")}>
            Normal Market
          </button>
          <button type="button" style={tab === "starline" ? tabActive : tabBtn} onClick={() => setTab("starline")}>
            King Starline
          </button>
        </div>

        <div style={grid}>
          {cards.map((card) => (
            <article key={card.key} style={rateCard}>
              <div style={cardHeader}>
                <span style={codeBadge}>{card.code}</span>
                <div style={{ flex: 1 }}>
                  <strong style={cardTitle}>{card.title}</strong>
                  <div style={cardSubtitle}>{card.subtitle}</div>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form[card.key] ?? ""}
                  onChange={(e) => setForm((c) => ({ ...c, [card.key]: e.target.value }))}
                  style={input}
                />
              </div>
            </article>
          ))}
        </div>
      </section>
    </AdminLayout>
  );
}

const page = { display: "grid", gap: "20px" };
const tabRow = { display: "flex", gap: 8 };
const tabBtn = { height: 40, padding: "0 20px", border: "1.5px solid #e0e7ff", borderRadius: 10, background: "#fff", color: "#64748b", fontWeight: 700, fontSize: 14, cursor: "pointer" };
const tabActive = { ...tabBtn, background: "linear-gradient(135deg,#4f46e5,#312e81)", borderColor: "#4f46e5", color: "#fff" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: 22, fontWeight: 900, color: "#1e1b4b" };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "13px" };
const modeBadge = { display: "inline-block", marginTop: "10px", padding: "6px 14px", borderRadius: "999px", background: "linear-gradient(135deg, #ede9fe, #ddd6fe)", color: "#4f46e5", fontSize: "11px", fontWeight: 900, letterSpacing: ".04em" };
const saveButton = { height: "46px", padding: "0 22px", border: 0, background: "linear-gradient(135deg, #4f46e5, #312e81)", color: "#fff", borderRadius: "12px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(79,70,229,0.30)" };
const grid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
const rateCard = { background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 16, padding: "18px 20px", borderLeft: "4px solid #4f46e5", boxShadow: "0 4px 20px rgba(79,70,229,0.08)", display: "flex", alignItems: "center", gap: "14px" };
const cardHeader = { display: "flex", alignItems: "center", gap: "14px", flex: 1 };
const codeBadge = { minWidth: "46px", height: "46px", display: "grid", placeItems: "center", borderRadius: "12px", background: "linear-gradient(135deg, #4f46e5, #312e81)", color: "#fff", fontWeight: 900, fontSize: "14px", letterSpacing: ".04em", flexShrink: 0 };
const cardTitle = { display: "block", fontSize: "15px", fontWeight: 800, color: "#1e1b4b" };
const cardSubtitle = { marginTop: "3px", color: "#64748b", fontSize: "12px" };
const label = { display: "grid", gap: "10px", color: "#334155", fontWeight: 700 };
const input = { width: "110px", height: "46px", border: "1.5px solid #e0e7ff", borderRadius: "12px", padding: "0 14px", fontSize: "16px", fontWeight: 800, background: "#f8faff", color: "#1e1b4b", outline: 0, flexShrink: 0 };

export default AdminGameRates;
